import { Outlet, useNavigate, Link } from "react-router-dom";
import { useState, useRef, useEffect, useMemo } from "react";
import type { Evaluation } from "@/types/evaluation";
import type { TemplateContent } from "@/types/template";
import { useTemplateStore } from "@/store/useTemplateStore";
import { useEvaluationStore } from "@/store/useEvaluationStore";
import { parseTemplate } from "@/parser/templateParser";
import { clearAll } from "@/store/db";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { BuildBadge } from "@/components/layout/BuildBadge";
import { HamburgerMenu } from "@/components/layout/HamburgerMenu";
import { AppDialogs } from "@/components/layout/AppDialogs";
import { useToast } from "@/hooks/useToast";
import { Wordmark } from "@/components/ui/Wordmark";

function useOutsideClick(
  ref: React.RefObject<HTMLDivElement | null>,
  active: boolean,
  onClose: () => void,
) {
  useEffect(() => {
    if (!active) {
      return;
    }
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, [active, ref, onClose]);
}

interface ParsedTemplate {
  id: string;
  parsed: TemplateContent;
}

interface AppHeaderProps {
  menuRef: React.RefObject<HTMLDivElement | null>;
  menuOpen: boolean;
  setMenuOpen: (v: boolean) => void;
  recentEvals: Evaluation[];
  evaluations: Evaluation[];
  menuTemplates: ParsedTemplate[];
  templates: { length: number };
  getParsedContent: (id: string) => TemplateContent | undefined;
  onNewEval: () => void;
  onNewTemplate: () => void;
  onClearDb: () => void;
}

function HamburgerContainer(props: Readonly<AppHeaderProps>) {
  const navigate = useNavigate();
  const handleMenuNavigate = (path: string) => {
    void navigate(path);
    props.setMenuOpen(false);
  };
  return (
    <div className="hamburger-container" ref={props.menuRef}>
      <button
        className="hamburger-btn"
        onClick={() => {
          props.setMenuOpen(!props.menuOpen);
        }}
        aria-label="Navigation menu"
      >
        &#9776;
      </button>
      {props.menuOpen && (
        <HamburgerMenu
          recentEvals={props.recentEvals}
          evaluationCount={props.evaluations.length}
          menuTemplates={props.menuTemplates}
          templateCount={props.templates.length}
          getParsedContent={props.getParsedContent}
          onNavigate={handleMenuNavigate}
          onNewEval={props.onNewEval}
          onNewTemplate={props.onNewTemplate}
          onClearDb={props.onClearDb}
        />
      )}
    </div>
  );
}

function AppHeader(props: Readonly<AppHeaderProps>) {
  return (
    <header className="app-header">
      <HamburgerContainer {...props} />
      <div className="app-logo">
        <Link to="/" className="app-logo-link" aria-label="NeatPT">
          <img src="/logo.svg" alt="" width="26" height="26" />
          <Wordmark />
        </Link>
      </div>
      <Breadcrumbs />
      <BuildBadge />
    </header>
  );
}

function useAppShellStores() {
  return {
    createEval: useEvaluationStore((s) => s.create),
    createTemplate: useTemplateStore((s) => s.create),
    evaluations: useEvaluationStore((s) => s.evaluations),
    getParsedContent: useTemplateStore((s) => s.getParsedContent),
    loadEvaluations: useEvaluationStore((s) => s.load),
    loadTemplates: useTemplateStore((s) => s.load),
    templates: useTemplateStore((s) => s.templates),
  };
}

function useAppShellActions(stores: ReturnType<typeof useAppShellStores>) {
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleCreateEval = async (templateId: string) => {
    const template = stores.templates.find((t) => t.id === templateId);
    if (!template) {
      return;
    }
    const evaluation = await stores.createEval(template);
    toast("Evaluation created", "success");
    void navigate(`/unified/${evaluation.id}`);
  };

  const handleClearDb = async () => {
    await clearAll();
    await stores.loadTemplates();
    await stores.loadEvaluations();
    void navigate("/");
    toast("All data cleared and reset", "success");
  };

  const handleCreateTemplate = async (name: string) => {
    const t = await stores.createTemplate(name, false);
    toast("Template created", "success");
    void navigate(`/build/${t.id}`);
  };

  return { handleClearDb, handleCreateEval, handleCreateTemplate };
}

function useAppShellDialogState() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showEvalPicker, setShowEvalPicker] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  return {
    menuOpen,
    menuRef,
    setMenuOpen,
    setShowClearConfirm,
    setShowEvalPicker,
    setShowPrompt,
    showClearConfirm,
    showEvalPicker,
    showPrompt,
  };
}

function useAppShellState() {
  const stores = useAppShellStores();
  const dialogs = useAppShellDialogState();
  useOutsideClick(dialogs.menuRef, dialogs.menuOpen, () => {
    dialogs.setMenuOpen(false);
  });

  const parsed = useMemo(
    () => stores.templates.map((t) => ({ ...t, parsed: parseTemplate(t.source).content })),
    [stores.templates],
  );

  const actions = useAppShellActions(stores);

  const handleCreateEval = async (templateId: string) => {
    await actions.handleCreateEval(templateId);
    dialogs.setShowEvalPicker(false);
    dialogs.setMenuOpen(false);
  };

  const handleClearDb = async () => {
    await actions.handleClearDb();
    dialogs.setMenuOpen(false);
    dialogs.setShowClearConfirm(false);
  };

  const handleCreateTemplate = async (name: string) => {
    await actions.handleCreateTemplate(name);
    dialogs.setShowPrompt(false);
    dialogs.setMenuOpen(false);
  };

  const recentEvals = [...stores.evaluations]
    .toSorted((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, 3);
  const menuTemplates = [...parsed]
    .toSorted((a, b) => a.parsed.name.localeCompare(b.parsed.name))
    .slice(0, 5);

  return {
    ...dialogs,
    evaluations: stores.evaluations,
    getParsedContent: stores.getParsedContent,
    handleClearDb,
    handleCreateEval,
    handleCreateTemplate,
    menuTemplates,
    parsed,
    recentEvals,
    templates: stores.templates,
  };
}

export function AppShell() {
  const s = useAppShellState();

  return (
    <div className="app-shell">
      <AppHeader
        menuRef={s.menuRef}
        menuOpen={s.menuOpen}
        setMenuOpen={s.setMenuOpen}
        recentEvals={s.recentEvals}
        evaluations={s.evaluations}
        menuTemplates={s.menuTemplates}
        templates={s.templates}
        getParsedContent={s.getParsedContent}
        onNewEval={() => {
          s.setShowEvalPicker(true);
          s.setMenuOpen(false);
        }}
        onNewTemplate={() => {
          s.setShowPrompt(true);
        }}
        onClearDb={() => {
          s.setShowClearConfirm(true);
        }}
      />
      <AppDialogs
        showEvalPicker={s.showEvalPicker}
        setShowEvalPicker={s.setShowEvalPicker}
        showClearConfirm={s.showClearConfirm}
        setShowClearConfirm={s.setShowClearConfirm}
        showPrompt={s.showPrompt}
        setShowPrompt={s.setShowPrompt}
        parsed={s.parsed}
        onCreateEval={s.handleCreateEval}
        onClearDb={s.handleClearDb}
        onCreateTemplate={s.handleCreateTemplate}
      />
      <main className="app-main">
        <Outlet />
      </main>
    </div>
  );
}
